import { BandModel } from './band-model';

/**
 * Jak pokazac dany model opaski. Jedna tabela zamiast szesciu map rozsypanych po
 * komponentach — dodanie modelu to jeden wiersz tutaj.
 *
 * Zastapila: bandTypeLabel, deviceWatchImages, deviceNameImages i deviceImageType
 * (karta pacjenta), getDeviceName() i bandAvatarType (oba ekrany konfiguracji) oraz
 * cztery warunki wyboru obrazka w show-avatar-band. Dwie z nich — deviceImageType
 * i bandAvatarType — istnialy wylacznie po to, zeby przetlumaczyc model na klucz do
 * kolejnej mapy, wiec zniknely bez sladu.
 */
export interface BandPresentation {
  /**
   * Naglowek w karcie pacjenta i nazwa modelu w magazynie opasek. Wersja "krzykliwa",
   * wielkimi literami. Marka, nie tekst do tlumaczenia: klucze `BACKEND.PRODUCT_*` maja
   * te sama nazwe przelozona w 18 jezykach („WICHTIGE PFLEGE" po niemiecku).
   */
  label: string;
  /** Nazwa w zdaniu, np. "SiDLY Care Pro" na ekranie konfiguracji. */
  name: string;
  /** Duza fotografia urzadzenia w panelu urzadzenia. */
  watch: string;
  /** Logotyp modelu obok fotografii. */
  wordmark: string;
  /** Mala ikonka na listach pacjentow (show-avatar-band). */
  avatar: string;
  /**
   * Ikonki rodziny ONE maja inne proporcje niz pozostale i wlasne klasy rozmiaru
   * (`small-one` / `smallest-one`). Easy Care pozycza dzis ten sam plik, wiec dzieli
   * tez sposob skalowania.
   */
  avatarSizing: 'default' | 'one';
}

const IMG = 'assets/images/';

export const BAND_PRESENTATION: { [model in BandModel]: BandPresentation } = {
  PLANNER: {
    label: 'Planner',
    name: 'Planner',
    watch: IMG + 'user_details_planner.png',
    wordmark: IMG + 'sidly%20planner%20name.png',
    avatar: IMG + 'user_details_planner.png',
    avatarSizing: 'default',
  },
  VITAL_CARE: {
    label: 'VITAL CARE',
    name: 'Vital Care',
    watch: IMG + 'user_details_vital.png',
    wordmark: IMG + 'sidly%20vital%20name.svg',
    avatar: IMG + 'vital_band.png',
    avatarSizing: 'default',
  },
  CARE_PRO: {
    label: 'CARE PRO',
    name: 'Care Pro',
    watch: IMG + 'user_details_care_pro.png',
    wordmark: IMG + 'sidly%20care%20name.svg',
    avatar: IMG + 'care2_small.png',
    avatarSizing: 'default',
  },
  ONE: {
    label: 'ONE',
    name: 'One',
    watch: IMG + 'user_details_one.png',
    wordmark: IMG + 'sidly%20one%20name.svg',
    avatar: IMG + 'one_black.png',
    avatarSizing: 'one',
  },
  EASY_CARE: {
    label: 'EASY CARE',
    name: 'Easy Care',
    // Zdjecie i ikonka: render dostarczony przez produkt (docs/guide/opaski/EasyCare.png),
    // ujecie 3/4 z zawinietym paskiem — ten sam styl co pozostale modele. Kadr dobrany
    // tak, zeby tresc wypelniala kanwe w 92%x93%, czyli tyle co vital_band.png: awatar
    // jest renderowany z `object-fit: fill` w kwadracie 30x30, wiec o postrzeganym
    // rozmiarze decyduje proporcja wypelnienia, a nie wymiary pliku.
    // Logotyp: wyciety z okladki instrukcji wyrobu tak ciasno (237x30), ze obie gorne
    // kropki "Y" w SiDLY wypadly poza kadr, a ogon "y" w slowie Easy zostal z jednym
    // wierszem zamiast trzech. Slowo SiDLY jest tu jednak co do piksela tym samym
    // logotypem co w sidly vital name.svg (skala 1.023), wiec zostalo zlozone z tamtego
    // WEKTORA zamiast dorysowywane; z rastra zostaje tylko "Easy | CARE", ktore kadr
    // objal w calosci. Kanwa 237x44 jak w pozostalych logotypach.
    watch: IMG + 'user_details_easy_care.png',
    wordmark: IMG + 'sidly%20easy%20care%20name.png',
    avatar: IMG + 'easy_care_small.png',
    avatarSizing: 'default',
  },
  // Model nierozpoznany: opaska bez wiersza w register_imei (getBandType odpowiada 400)
  // albo odpowiedz jeszcze w drodze. Zachowuje sie dokladnie tak, jak przed scaleniem
  // map — a to znaczy NIESPOJNIE: obrazki Care Pro, ale nazwa "Vital Care", bo taki byl
  // `default` w getDeviceName(). Zostawione bez zmiany celowo; do rozstrzygniecia osobno.
  UNKNOWN: {
    label: '',
    name: 'Vital Care',
    watch: IMG + 'user_details_care_pro.png',
    wordmark: IMG + 'sidly%20care%20name.svg',
    avatar: IMG + 'care2_small.png',
    avatarSizing: 'default',
  },
};

export function presentationOf(model: BandModel): BandPresentation {
  return BAND_PRESENTATION[model] || BAND_PRESENTATION.UNKNOWN;
}
